import { ETLCourse, ETLModule, ETLQuiz } from '@/data/etl/types';
import { Course, Module } from '@/data/courses/types';

// Define QuickCheck interface to match the existing structure
interface QuickCheck {
  questions: Array<{
    id: string;
    question: string;
    options: string[];
    correctAnswer: number;
    correctAnswerIndex?: number;
    difficulty?: 'easy' | 'medium' | 'hard';
    explanation?: string;
    points?: number;
    type?: string;
  }>;
  passThreshold?: number;
  timeLimit?: number;
}

/**
 * Converts ETL Quiz format to standard QuickCheck format
 */
export function adaptETLQuizToQuickCheck(etlQuiz: ETLQuiz): QuickCheck {
  return {
    questions: etlQuiz.questions.map(q => ({
      id: q.id,
      question: q.question,
      options: q.options,
      correctAnswer: q.correctAnswer,
      explanation: q.explanation,
      difficulty: q.difficulty || 'medium',
      points: q.points || 1,
      type: q.type || 'multiple-choice'
    })),
    passThreshold: etlQuiz.passThreshold || 80,
    timeLimit: etlQuiz.timeLimit
  };
}

/**
 * Converts ETL Module to standard Module format
 */
export function adaptETLModuleToModule(etlModule: ETLModule): Module {
  return {
    id: etlModule.id,
    title: etlModule.title,
    description: etlModule.description,
    content: etlModule.content,
    duration: etlModule.duration,
    difficulty: etlModule.difficulty,
    order: etlModule.order,
    estimatedTime: etlModule.estimatedTime,
    // Map ETL objectives to standard objectives
    objectives: etlModule.objectives || etlModule.learningObjectives || [],
    learningObjectives: etlModule.learningObjectives || etlModule.objectives || [],
    prerequisites: etlModule.prerequisites,
    codeExamples: etlModule.codeExamples,
    // Convert ETL quiz to standard quickCheck
    quickCheck: etlModule.quiz ? adaptETLQuizToQuickCheck(etlModule.quiz) : undefined,
    // Keep quiz as undefined for standard interface compatibility
    quiz: undefined,
    // Map practice if it exists (using the correct property name)
    practice: etlModule.practice ? {
      task: etlModule.practice.task,
      starter: etlModule.practice.starter,
      solution: etlModule.practice.solution,
      hints: etlModule.practice.hints
    } : undefined
  };
}

/**
 * Converts ETL Course to standard Course format
 */
export function adaptETLCourseToCourse(etlCourse: ETLCourse): Course {
  return {
    id: etlCourse.id,
    title: etlCourse.title,
    description: etlCourse.description,
    level: etlCourse.level,
    totalDuration: etlCourse.totalDuration,
    image: etlCourse.image,
    prerequisites: etlCourse.prerequisites,
    learningOutcomes: etlCourse.learningOutcomes,
    modules: etlCourse.modules.map(adaptETLModuleToModule),
    type: 'etl' as any,
    // Add ETL-specific metadata
    tags: [etlCourse.toolFocus, 'ETL', 'Data Engineering'],
    requirements: etlCourse.prerequisites
  };
}
